import {useContext, useEffect, useRef} from "react";
import {useTranslation} from "react-i18next";
// RISKS.md/P2.2: AppContext.tsx imports this file, so these are uninitialised at module scope.
import AppContext, {SidebarType} from "../AppContext";
import NotificationContext from "../NotificationContext";
import type {ConferenceUserWithStatus} from "@inpriton/types/conferenceTypes";
import {escapeHtml} from "../../helper/escapeHtml";
import {notifyWsFailure} from "../../lib/notifyWsFailure";
import {isWaiting} from "../../helper/presence";

// RISKS.md/P2.15: a snapshot naming a producer the peer has already replaced fails once and is
// corrected by the next push milliseconds later — undebounced, that alarms a healthy meeting.
const CONSUME_FAILURE_TOAST_MS = 3000

// RISKS.md/P2.15: one toast for the room, never one per peer — it names nobody, so N failed tracks
// cannot become N toasts, and no peer-supplied name reaches a body rendered as HTML (RISKS.md/P0.7).
const ConsumeFailureNotifier = (props: { failed: boolean, onRetry: () => void }) => {
  const { t } = useTranslation()
  const { createNotification, hideNotification, clearGeneration } = useContext(NotificationContext)

  useEffect(() => {
    if (!props.failed) {
      hideNotification('consume-failed')
      return
    }
    const timer = setTimeout(() => createNotification({
      id: 'consume-failed',
      message: t('chat.receiveFailed'),
      persistent: true,
      actions: [{ label: t('lobby.retry'), onClick: props.onRetry }],
    }), CONSUME_FAILURE_TOAST_MS)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.failed, clearGeneration])

  return null
}

const ModeratorAdmitNotifier = () => {
  const { t } = useTranslation()
  const { users, userData, ws, currentRoom, setSidebar } = useContext(AppContext)
  const { createNotification, hideNotification } = useContext(NotificationContext)
  // Track which waiting users we've already toasted (by uuid). This makes the request toast fire
  // for EVERYONE already waiting when the host joins LATE (they're in the initial roster, not
  // "new"), while roster churn or the host dismissing the toast (X) does not re-toast a
  // still-waiting user. Depending on userData?.isModerator too means a host whose moderator flag
  // resolves after the roster still gets the toasts.
  const notifiedRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    if (!userData?.isModerator) {
      return
    }
    users
      .filter(u => isWaiting(u) && !notifiedRef.current.has(u.uuid))
      .forEach(user => {
        notifiedRef.current.add(user.uuid)
        createNotification({
          id: `admit-${user.uuid}`,
          message: `<strong>${escapeHtml(user.name || user.email)}</strong> möchte an Ihrer Sitzung teilnehmen.`,
          persistent: true,
          actions: [
            // Admit — the same action as the participant sidebar's Admit link. The notification
            // button wrapper dismisses the toast afterwards, so a failure needs its own toast or
            // the host is left believing the click worked (RISKS.md/P2.15).
            { label: 'Zulassen', onClick: () => {
              ws?.admitUser(currentRoom as string, user.uuid)
                .catch(e => {
                  // Logged as well: the failure toast carries a `ttl`, so in ten seconds nothing
                  // would be left to diagnose from.
                  console.error('admit failed', e)
                  notifyWsFailure({ createNotification, hideNotification }, e, {
                    id: 'admit-failed',
                    message: t('sidebar.participants.admitFailed'),
                    ttl: 10000,
                  })
                })
            } },
            // Open the participant sidebar to review; the wrapper dismisses the toast too.
            { label: 'Anzeigen', onClick: () => setSidebar(SidebarType.Participants) },
          ],
        })
      })
    // Withdraw and forget anyone no longer waiting, so a later request toasts again.
    // A still-waiting user the host X-dismissed stays remembered, so it is not re-toasted.
    const waiting = new Set(users.filter(isWaiting).map(u => u.uuid))
    notifiedRef.current.forEach(uuid => {
      if (waiting.has(uuid)) return
      hideNotification(`admit-${uuid}`)
      notifiedRef.current.delete(uuid)
    })
  }, [users, userData?.isModerator])

  return null
}


// After a moderator kicks someone, the target vanishes from the roster (the user:removed flow).
// Watch for that disappearance and show EVERYONE still in the meeting (participants, interpreters
// AND moderators/host) a snackbar naming who was removed. Diffing the roster — rather than
// reacting to the raw event — means the two server broadcasts per kick (removeUser + the
// leave-handler follow-up) collapse into a single toast, and gives us the removed user's display
// name from the pre-removal roster.
const RemovalNotifier = () => {
  const { t } = useTranslation()
  const { users, conferenceStatus } = useContext(AppContext)
  const { createNotification } = useContext(NotificationContext)
  const prevUsersRef = useRef<ConferenceUserWithStatus[]>([])

  useEffect(() => {
    // Only notify clients that have ALREADY JOINED the meeting (been admitted). A user still
    // waiting for admission in the green room has no meeting context, so a removal notice is just
    // noise for them. prevUsersRef is still updated below regardless, so a user admitted later
    // does not get a burst of notifications for removals that happened while they were waiting.
    if (conferenceStatus.admitted) {
      const currentUuids = new Set(users.map(u => u.uuid))
      prevUsersRef.current
        .filter(u => !currentUuids.has(u.uuid))
        .forEach(removedUser => {
          createNotification({
            id: `user-removed-${removedUser.uuid}`,
            // i18next runs with escapeValue:false and the notification body renders via
            // dangerouslySetInnerHTML, so escape the user-entered name to prevent markup injection.
            message: t('chat.removedNotice', { name: escapeHtml(removedUser.name || removedUser.email) }),
            ttl: 10000,
          })
        })
    }
    prevUsersRef.current = users
  }, [users])

  return null
}

// A lost connection reaches us as the same offline flag as the Leave button or a closed tab, and is
// usually back within seconds, so only an absence this long is announced, and a return inside it is not.
const DEPARTURE_GRACE_MS = 15000
const PRESENCE_TOAST_MS = 10000

// Present means online and admitted, so a participant or interpreter enters on admission and an
// organizer on joining. A kick drops the user from the roster instead: RemovalNotifier announces it.
// Organizers get no entry toasts, having the admission request for participants and interpreters.
const PresenceNotifier = () => {
  const { t } = useTranslation()
  const { users, userData, conferenceStatus, wsConnected } = useContext(AppContext)
  const { createNotification, hideNotification } = useContext(NotificationContext)
  const prevUsersRef = useRef<ConferenceUserWithStatus[]>([])
  const usersRef = useRef(users)
  usersRef.current = users
  // Who left and is not announced yet, kept apart from the timers: those stop while our own socket
  // is down, as no return could reach us, and restart once it is back.
  const awayRef = useRef(new Set<string>())
  const timersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>())
  // Our entry's roster lists everyone already there, and none of them has just entered.
  const wasInMeetingRef = useRef(false)

  const inMeeting = !!conferenceStatus.admitted && wsConnected

  useEffect(() => {
    const timers = timersRef.current
    return () => timers.forEach(clearTimeout)
  }, [])

  useEffect(() => {
    const away = awayRef.current
    const timers = timersRef.current
    const forget = (uuid: string) => {
      clearTimeout(timers.get(uuid))
      timers.delete(uuid)
      away.delete(uuid)
    }
    const isPresent = (u?: ConferenceUserWithStatus) => !!u && u.online && !!u.admitted
    // RISKS.md/P0.7: the body renders as HTML, and the name is user-entered.
    const nameOf = (u: ConferenceUserWithStatus) => escapeHtml(u.name || u.email)

    const entering = inMeeting && !wasInMeetingRef.current
    wasInMeetingRef.current = inMeeting
    const announceEntries = inMeeting && !entering && !userData?.isModerator

    const before = new Map(prevUsersRef.current.map(u => [u.uuid, u]))
    prevUsersRef.current = users
    users.forEach(user => {
      if (user.uuid === userData?.uuid) {
        return
      }
      if (isPresent(user)) {
        const returning = away.has(user.uuid)
        forget(user.uuid)
        if (isPresent(before.get(user.uuid))) {
          return
        }
        hideNotification(`user-left-${user.uuid}`)
        if (announceEntries && !returning) {
          createNotification({
            id: `user-joined-${user.uuid}`,
            message: t('notifications.userJoined', { name: nameOf(user) }),
            ttl: PRESENCE_TOAST_MS,
          })
        }
      } else if (isPresent(before.get(user.uuid))) {
        away.add(user.uuid)
      }
    })
    away.forEach(uuid => {
      if (!users.some(u => u.uuid === uuid)) {
        forget(uuid)
      }
    })

    if (!inMeeting) {
      timers.forEach(clearTimeout)
      timers.clear()
      return
    }
    away.forEach(uuid => {
      if (timers.has(uuid)) {
        return
      }
      timers.set(uuid, setTimeout(() => {
        const current = usersRef.current.find(u => u.uuid === uuid)
        forget(uuid)
        if (!current || isPresent(current)) {
          return
        }
        hideNotification(`user-joined-${uuid}`)
        createNotification({
          id: `user-left-${uuid}`,
          message: t('notifications.userLeft', { name: nameOf(current) }),
          ttl: PRESENCE_TOAST_MS,
        })
      }, DEPARTURE_GRACE_MS))
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, inMeeting])

  return null
}

export {
  ConsumeFailureNotifier,
  ModeratorAdmitNotifier,
  PresenceNotifier,
  RemovalNotifier,
}
